import { useMemo } from "react";

import { useMutation } from "@tanstack/react-query";

import { updateUserSettings } from "@app/analytics/preferences/api";
import { useAuth } from "@app/common/auth/useAuth";
import { User, UserMetadata } from "@app/common/types";
import { combineUserMetadata } from "@app/common/utils";

const DEFAULT_USER_METADATA: UserMetadata = {
  autoPlayMeetingReportVideoOnLoad: true,
  onboardingChecklistProgress: 0,
};

const getUserMetadata = (user: User | null) => {
  if (!user) return;

  // combine user metadata with defaults
  return {
    ...DEFAULT_USER_METADATA,
    ...(user.userMetadata ?? {}),
  };
};

const useUserMetadata = () => {
  const { user, fetchAndSetUser, setUser, impersonating } = useAuth();

  const userMetadata = useMemo(() => getUserMetadata(user), [user]);

  const updateUserMetadata = useMutation({
    mutationFn: async (metadata: Partial<UserMetadata>) => {
      // don't update a user's metadata while impersonating
      if (!user || impersonating) return;
      await updateUserSettings(combineUserMetadata(user, metadata));
    },
    retry: 3, // ok to retry since this mutation is idempotent
    onMutate: (metadata) => {
      if (!user) return;
      const { userMetadata: updatedMetadata } = combineUserMetadata(
        user,
        metadata
      );
      const originalMetadata = user.userMetadata ?? {};
      setUser({ ...user, userMetadata: updatedMetadata });
      return { originalMetadata };
    },
    onSettled: fetchAndSetUser,
    onError: (_error, _body, context) => {
      if (context?.originalMetadata && user) {
        setUser({ ...user, userMetadata: context.originalMetadata ?? {} });
      }
    },
  });

  return { userMetadata, updateUserMetadata };
};

export default useUserMetadata;
